import {
    ChevronDown,
    CircleUser,
    Globe,
    LayoutGrid,
    Menu,
    X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { resolvePublicMediaUrl } from '../config/api';
import {
    HEADER_MEGA_MENUS,
    HEADER_SIMPLE_LINKS,
    headerPathMatches,
} from '../data/headerNavigation';
import { MEMBER_AREA_NAV_ITEMS } from '../data/memberAreaContent';

/** @param {{ to: string, className?: string, children: unknown, onNavigate?: () => void }} props */
function HeaderMenuLink({ to, className = '', children, onNavigate }) {
    const external = /^https?:\/\//i.test(to);
    if (external) {
        return (
            <a
                href={to}
                className={className}
                target="_blank"
                rel="noopener noreferrer"
                onClick={onNavigate}
            >
                {children}
            </a>
        );
    }
    return (
        <Link to={to} className={className} onClick={onNavigate}>
            {children}
        </Link>
    );
}

const SCROLL_DELTA = 8;

/**
 * Aynı tıklamada dışarı-tıkla dinleyicisinin menüyü hemen kapatmasını önler.
 * @param {boolean} active
 * @param {'mousedown' | 'click'} type
 * @param {(e: Event) => void} handler
 */
function useDeferredDocumentListener(active, type, handler) {
    const handlerRef = useRef(handler);
    handlerRef.current = handler;

    useEffect(() => {
        if (!active) return undefined;
        /** @type {((e: Event) => void) | null} */
        let listener = null;
        const timer = window.setTimeout(() => {
            listener = (e) => handlerRef.current(e);
            document.addEventListener(type, listener);
        }, 0);
        return () => {
            window.clearTimeout(timer);
            if (listener) {
                document.removeEventListener(type, listener);
            }
        };
    }, [active, type]);
}

export function Header() {
    const { t, i18n } = useTranslation();
    const { isAuthenticated, user, logout } = useAuth();
    const isEn = i18n.language?.toLowerCase().startsWith('en');
    const langBadge = isEn ? 'EN' : 'TR';
    const [megaOpen, setMegaOpen] = useState(
        /** @type {string | null} */(null),
    );
    const [accountMenuOpen, setAccountMenuOpen] = useState(false);
    const [memberAreaOpen, setMemberAreaOpen] = useState(false);
    const [mobileNavOpen, setMobileNavOpen] = useState(false);
    const [memberPanelAccordionOpen, setMemberPanelAccordionOpen] =
        useState(false);
    const [isDesktopNav, setIsDesktopNav] = useState(
        () =>
            typeof window !== 'undefined' &&
            window.matchMedia('(min-width: 992px)').matches,
    );
    const [scrollHidden, setScrollHidden] = useState(false);
    const closeTimerRef = useRef(/** @type {number | null} */(null));
    const headerRef = useRef(/** @type {HTMLElement | null} */(null));
    const accountWrapRef = useRef(/** @type {HTMLDivElement | null} */(null));
    const memberAreaWrapRef = useRef(/** @type {HTMLDivElement | null} */(null));
    const lastScrollYRef = useRef(0);
    const megaOpenRef = useRef(megaOpen);
    megaOpenRef.current = megaOpen;
    const location = useLocation();

    const clearCloseTimer = useCallback(() => {
        if (closeTimerRef.current != null) {
            window.clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
    }, []);

    const scheduleCloseMega = useCallback(() => {
        clearCloseTimer();
        closeTimerRef.current = window.setTimeout(() => {
            setMegaOpen(null);
            closeTimerRef.current = null;
        }, 280);
    }, [clearCloseTimer]);

    const openMegaHover = useCallback(
        (id) => {
            clearCloseTimer();
            setMegaOpen(id);
        },
        [clearCloseTimer],
    );

    const closeMegaNow = useCallback(() => {
        clearCloseTimer();
        setMegaOpen(null);
    }, [clearCloseTimer]);

    const closeMobileNav = useCallback(() => {
        setMobileNavOpen(false);
        setMemberPanelAccordionOpen(false);
    }, []);

    const handleMenuNavigate = useCallback(() => {
        closeMegaNow();
        closeMobileNav();
    }, [closeMegaNow, closeMobileNav]);

    useEffect(() => {
        return () => clearCloseTimer();
    }, [clearCloseTimer]);

    useEffect(() => {
        setMegaOpen(null);
        setAccountMenuOpen(false);
        setMemberAreaOpen(false);
        setMobileNavOpen(false);
        setMemberPanelAccordionOpen(false);
        setScrollHidden(false);
    }, [location.pathname]);

    useEffect(() => {
        if (megaOpen) {
            setScrollHidden(false);
            setAccountMenuOpen(false);
            setMemberAreaOpen(false);
        }
    }, [megaOpen]);

    useEffect(() => {
        const el = headerRef.current;
        if (!el) return undefined;

        const setHeightVar = () => {
            const isLg = window.matchMedia('(min-width: 992px)').matches;
            const surface = el.querySelector('.site-header-surface');
            const h = isLg
                ? el.offsetHeight
                : (surface?.offsetHeight ?? el.offsetHeight);
            document.documentElement.style.setProperty(
                '--site-header-height',
                `${h}px`,
            );
        };

        setHeightVar();
        const ro = new ResizeObserver(() => setHeightVar());
        ro.observe(el);
        const surface = el.querySelector('.site-header-surface');
        if (surface) ro.observe(surface);
        window.addEventListener('resize', setHeightVar);

        return () => {
            ro.disconnect();
            window.removeEventListener('resize', setHeightVar);
            document.documentElement.style.removeProperty(
                '--site-header-height',
            );
        };
    }, [isAuthenticated]);

    useEffect(() => {
        lastScrollYRef.current = window.scrollY;

        const onScroll = () => {
            if (megaOpenRef.current) return;

            const y = window.scrollY;
            const prev = lastScrollYRef.current;
            const delta = y - prev;
            lastScrollYRef.current = y;

            if (y < 56) {
                setScrollHidden(false);
                return;
            }
            if (delta > SCROLL_DELTA) {
                setScrollHidden(true);
            } else if (delta < -SCROLL_DELTA) {
                setScrollHidden(false);
            }
        };

        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    useEffect(() => {
        const syncHeaderStickyTop = () => {
            const isLg = window.matchMedia('(min-width: 992px)').matches;
            if (scrollHidden && isLg) {
                document.documentElement.setAttribute(
                    'data-header-scroll-hidden',
                    '',
                );
            } else {
                document.documentElement.removeAttribute(
                    'data-header-scroll-hidden',
                );
            }
        };

        syncHeaderStickyTop();
        const mq = window.matchMedia('(min-width: 992px)');
        mq.addEventListener('change', syncHeaderStickyTop);
        return () => {
            mq.removeEventListener('change', syncHeaderStickyTop);
            document.documentElement.removeAttribute(
                'data-header-scroll-hidden',
            );
        };
    }, [scrollHidden]);

    useEffect(() => {
        if (!megaOpen) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') setMegaOpen(null);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [megaOpen]);

    useEffect(() => {
        if (!accountMenuOpen) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') setAccountMenuOpen(false);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [accountMenuOpen]);

    useDeferredDocumentListener(accountMenuOpen, 'mousedown', (e) => {
        const t = e.target;
        if (t instanceof Node && accountWrapRef.current?.contains(t)) {
            return;
        }
        setAccountMenuOpen(false);
    });

    useEffect(() => {
        if (!memberAreaOpen) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') setMemberAreaOpen(false);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [memberAreaOpen]);

    useDeferredDocumentListener(memberAreaOpen, 'mousedown', (e) => {
        const t = e.target;
        if (t instanceof Node && memberAreaWrapRef.current?.contains(t)) {
            return;
        }
        setMemberAreaOpen(false);
    });

    useDeferredDocumentListener(Boolean(megaOpen), 'mousedown', (e) => {
        const t = e.target;
        if (!(t instanceof Element)) return;
        if (headerRef.current?.contains(t)) return;
        setMegaOpen(null);
    });

    useEffect(() => {
        if (!megaOpen) return;
        const mq = window.matchMedia('(max-width: 991.98px)');
        if (!mq.matches) return;
        const el = document.getElementById('header-mega-panel');
        el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, [megaOpen]);

    useEffect(() => {
        if (!megaOpen) return undefined;
        const mq = window.matchMedia('(min-width: 992px)');
        if (!mq.matches) return undefined;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prev;
        };
    }, [megaOpen]);

    useEffect(() => {
        if (!mobileNavOpen) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') setMobileNavOpen(false);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [mobileNavOpen]);

    useEffect(() => {
        if (!mobileNavOpen) return undefined;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prev;
        };
    }, [mobileNavOpen]);

    useEffect(() => {
        if (mobileNavOpen) setScrollHidden(false);
    }, [mobileNavOpen]);

    useEffect(() => {
        const mq = window.matchMedia('(min-width: 992px)');
        const sync = () => setIsDesktopNav(mq.matches);
        sync();
        mq.addEventListener('change', sync);
        return () => mq.removeEventListener('change', sync);
    }, []);

    useEffect(() => {
        if (isDesktopNav) setMobileNavOpen(false);
    }, [isDesktopNav]);

    const displayName =
        user?.name ||
        user?.email?.split('@')[0] ||
        t('header.displayNameFallback');

    const accountAvatarSrc = useMemo(() => {
        const raw = user?.profilePictureUrl;
        if (!raw || typeof raw !== 'string' || !raw.trim()) return '';
        return resolvePublicMediaUrl(raw);
    }, [user?.profilePictureUrl]);

    const [accountAvatarBroken, setAccountAvatarBroken] = useState(false);
    useEffect(() => {
        setAccountAvatarBroken(false);
    }, [accountAvatarSrc]);

    const showAccountAvatar =
        Boolean(accountAvatarSrc) && !accountAvatarBroken;

    const activeMega = HEADER_MEGA_MENUS.find((m) => m.id === megaOpen);
    const { pathname } = location;

    const handleMenuMouseLeave = useCallback(() => {
        if (window.matchMedia('(min-width: 992px)').matches) {
            scheduleCloseMega();
        }
    }, [scheduleCloseMega]);

    return (
        <header
            ref={headerRef}
            className={`site-header${scrollHidden ? ' site-header--scroll-hidden' : ''}${mobileNavOpen ? ' site-header--mobile-nav-open' : ''}`}
        >
            {megaOpen ? (
                <div className="header-mega-backdrop" aria-hidden />
            ) : null}
            {mobileNavOpen ? (
                <button
                    type="button"
                    className="header-offcanvas-backdrop"
                    aria-label={t('header.a11y.closeMenu')}
                    onClick={closeMobileNav}
                />
            ) : null}
            <div className="site-header-surface">
                <nav className="header navbar py-3">
                    <div className="container">
                        <div className="d-flex align-items-center w-100">
                            <Link
                                className="navbar-brand m-0 d-flex align-items-center"
                                to="/"
                            >
                                <img
                                    src="/assets/images/rotary-logo.svg"
                                    alt={t('header.rotaryAlt')}
                                    className="header-logo"
                                />
                            </Link>
                            <div className="d-flex align-items-center gap-1 gap-sm-2 ms-auto flex-shrink-0 header-toolbar-actions">
                                <div className="header-toolbar-desktop d-none d-lg-flex align-items-center gap-1 gap-sm-2">
                                <button
                                    type="button"
                                    className="btn btn-link text-decoration-none p-2 d-flex align-items-center"
                                    aria-label={
                                        isEn
                                            ? t('header.a11y.switchToTr')
                                            : t('header.a11y.switchToEn')
                                    }
                                    onClick={() => {
                                        void i18n.changeLanguage(
                                            isEn ? 'tr' : 'en',
                                        );
                                    }}
                                >
                                    <Globe
                                        size={20}
                                        strokeWidth={1.75}
                                        aria-hidden
                                    />
                                    <span className="small fw-semibold ms-2">
                                        {langBadge}
                                    </span>
                                </button>
                                {isAuthenticated ? (
                                    <div
                                        className="header-account-wrap position-relative"
                                        ref={accountWrapRef}
                                    >
                                        <button
                                            type="button"
                                            className="btn btn-link p-2 d-flex align-items-center text-decoration-none gap-1"
                                            aria-label={t(
                                                'header.accountMenuLabel',
                                                { name: displayName },
                                            )}
                                            aria-expanded={accountMenuOpen}
                                            aria-haspopup="true"
                                            aria-controls="header-account-menu"
                                            id="header-account-trigger"
                                            onClick={() => {
                                                setMemberAreaOpen(false);
                                                setAccountMenuOpen((o) => !o);
                                            }}
                                        >
                                            {showAccountAvatar ? (
                                                <img
                                                    src={accountAvatarSrc}
                                                    alt=""
                                                    width={28}
                                                    height={28}
                                                    className="header-account-avatar rounded-circle flex-shrink-0"
                                                    decoding="async"
                                                    onError={() =>
                                                        setAccountAvatarBroken(
                                                            true,
                                                        )
                                                    }
                                                />
                                            ) : (
                                                <CircleUser
                                                    size={22}
                                                    aria-hidden
                                                    className="flex-shrink-0"
                                                />
                                            )}
                                            <span className="small fw-semibold ms-1 text-truncate header-account-name">
                                                {displayName}
                                            </span>
                                            <ChevronDown
                                                size={18}
                                                strokeWidth={2}
                                                aria-hidden
                                                className={`header-account-chevron flex-shrink-0${accountMenuOpen ? ' is-open' : ''}`}
                                            />
                                        </button>
                                        {accountMenuOpen ? (
                                            <div
                                                id="header-account-menu"
                                                role="menu"
                                                aria-labelledby="header-account-trigger"
                                                className="header-account-dropdown"
                                            >
                                                <Link
                                                    to="/profile"
                                                    role="menuitem"
                                                    className="header-account-dropdown-action"
                                                    onClick={() =>
                                                        setAccountMenuOpen(false)
                                                    }
                                                >
                                                    {t('header.profile')}
                                                </Link>
                                                <Link
                                                    to="/change-password"
                                                    role="menuitem"
                                                    className="header-account-dropdown-action"
                                                    onClick={() =>
                                                        setAccountMenuOpen(false)
                                                    }
                                                >
                                                    {t('header.changePassword')}
                                                </Link>
                                                <button
                                                    type="button"
                                                    role="menuitem"
                                                    className="header-account-dropdown-logout"
                                                    onClick={() => {
                                                        setAccountMenuOpen(
                                                            false,
                                                        );
                                                        logout();
                                                        toast.success(
                                                            t(
                                                                'header.logoutToast',
                                                            ),
                                                            {
                                                                autoClose: 2800,
                                                            },
                                                        );
                                                    }}
                                                >
                                                    {t('header.logout')}
                                                </button>
                                            </div>
                                        ) : null}
                                    </div>
                                ) : (
                                    <Link
                                        to="/login"
                                        className="btn btn-link p-2 d-flex align-items-center text-decoration-none"
                                        aria-label={t('header.memberLogin')}
                                    >
                                        <CircleUser size={22} aria-hidden />
                                        <span className="small fw-semibold ms-2">
                                            {t('header.memberLogin')}
                                        </span>
                                    </Link>
                                )}
                                {isAuthenticated ? (
                                    <div
                                        className="header-member-area-wrap position-relative flex-shrink-0"
                                        ref={memberAreaWrapRef}
                                    >
                                        <button
                                            type="button"
                                            className="header-member-area-trigger d-flex align-items-center gap-1"
                                            aria-label={t('header.memberPanel')}
                                            aria-expanded={memberAreaOpen}
                                            aria-haspopup="true"
                                            aria-controls="header-member-area-menu"
                                            id="header-member-area-trigger"
                                            onClick={() => {
                                                setAccountMenuOpen(false);
                                                setMemberAreaOpen((o) => !o);
                                            }}
                                        >
                                            <LayoutGrid
                                                size={17}
                                                strokeWidth={2}
                                                aria-hidden
                                                className="header-member-area-trigger-icon flex-shrink-0"
                                            />
                                            <span className="header-member-area-trigger-text">
                                                {t('header.memberPanel')}
                                            </span>
                                            <ChevronDown
                                                size={16}
                                                strokeWidth={2}
                                                aria-hidden
                                                className={`header-member-area-chevron flex-shrink-0${memberAreaOpen ? ' is-open' : ''}`}
                                            />
                                        </button>
                                        {memberAreaOpen ? (
                                            <div
                                                id="header-member-area-menu"
                                                role="menu"
                                                aria-labelledby="header-member-area-trigger"
                                                className="header-member-area-dropdown"
                                            >
                                                {MEMBER_AREA_NAV_ITEMS.map(
                                                    (item) => (
                                                        <NavLink
                                                            key={item.to}
                                                            role="menuitem"
                                                            to={item.to}
                                                            className={({
                                                                isActive,
                                                            }) =>
                                                                `header-member-area-link${isActive ? ' active' : ''}`
                                                            }
                                                            onClick={() =>
                                                                setMemberAreaOpen(
                                                                    false,
                                                                )
                                                            }
                                                        >
                                                            {t(item.labelKey)}
                                                        </NavLink>
                                                    ),
                                                )}
                                            </div>
                                        ) : null}
                                    </div>
                                ) : null}
                                </div>
                                <button
                                    type="button"
                                    className="header-mobile-menu-btn btn btn-link p-2 d-flex align-items-center justify-content-center d-lg-none"
                                    aria-label={
                                        mobileNavOpen
                                            ? t('header.a11y.closeMenu')
                                            : t('header.a11y.openMenu')
                                    }
                                    aria-expanded={mobileNavOpen}
                                    aria-controls="header-mobile-nav"
                                    onClick={() => {
                                        setAccountMenuOpen(false);
                                        setMemberAreaOpen(false);
                                        setMemberPanelAccordionOpen(false);
                                        setMobileNavOpen((open) => !open);
                                    }}
                                >
                                    {mobileNavOpen ? (
                                        <X size={24} strokeWidth={2} aria-hidden />
                                    ) : (
                                        <Menu size={24} strokeWidth={2} aria-hidden />
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </nav>
            </div>
            <div
                id="header-mobile-nav"
                className={`header-nav-shell${mobileNavOpen ? ' is-open' : ''}`}
                aria-hidden={isDesktopNav ? undefined : !mobileNavOpen}
                onMouseEnter={() => {
                    if (window.matchMedia('(min-width: 992px)').matches) {
                        clearCloseTimer();
                    }
                }}
                onMouseLeave={handleMenuMouseLeave}
            >
                <div className="header-offcanvas-top d-lg-none">
                    <span className="header-offcanvas-title">
                        {t('header.menuTitle')}
                    </span>
                    <button
                        type="button"
                        className="header-offcanvas-close btn btn-link p-2"
                        aria-label={t('header.a11y.closeMenu')}
                        onClick={closeMobileNav}
                    >
                        <X size={22} strokeWidth={2} aria-hidden />
                    </button>
                </div>
                <div className="header-offcanvas-actions d-lg-none">
                    <button
                        type="button"
                        className="header-offcanvas-lang btn btn-link text-decoration-none w-100 d-flex align-items-center"
                        aria-label={
                            isEn
                                ? t('header.a11y.switchToTr')
                                : t('header.a11y.switchToEn')
                        }
                        onClick={() => {
                            void i18n.changeLanguage(isEn ? 'tr' : 'en');
                        }}
                    >
                        <Globe
                            size={20}
                            strokeWidth={1.75}
                            aria-hidden
                            className="flex-shrink-0"
                        />
                        <span className="small fw-semibold ms-2">
                            {isEn
                                ? t('header.a11y.switchToTr')
                                : t('header.a11y.switchToEn')}
                        </span>
                        <span className="header-offcanvas-lang-badge ms-auto">
                            {langBadge}
                        </span>
                    </button>
                    {isAuthenticated ? (
                        <div className="header-offcanvas-user-card">
                            <div className="header-offcanvas-account">
                                <div className="header-offcanvas-account-head">
                                    {showAccountAvatar ? (
                                        <img
                                            src={accountAvatarSrc}
                                            alt=""
                                            width={40}
                                            height={40}
                                            className="header-account-avatar rounded-circle flex-shrink-0"
                                            decoding="async"
                                            onError={() =>
                                                setAccountAvatarBroken(true)
                                            }
                                        />
                                    ) : (
                                        <CircleUser
                                            size={32}
                                            aria-hidden
                                            className="flex-shrink-0 text-secondary"
                                        />
                                    )}
                                    <div className="header-offcanvas-account-meta min-w-0">
                                        <p className="header-offcanvas-account-name mb-0">
                                            {displayName}
                                        </p>
                                        {user?.email ? (
                                            <p className="header-offcanvas-account-email small text-secondary mb-0">
                                                {user.email}
                                            </p>
                                        ) : null}
                                    </div>
                                </div>
                                <Link
                                    to="/profile"
                                    className="header-offcanvas-action"
                                    onClick={closeMobileNav}
                                >
                                    {t('header.profile')}
                                </Link>
                                <Link
                                    to="/change-password"
                                    className="header-offcanvas-action"
                                    onClick={closeMobileNav}
                                >
                                    {t('header.changePassword')}
                                </Link>
                                <button
                                    type="button"
                                    className="header-offcanvas-action header-offcanvas-action--logout"
                                    onClick={() => {
                                        closeMobileNav();
                                        logout();
                                        toast.success(t('header.logoutToast'), {
                                            autoClose: 2800,
                                        });
                                    }}
                                >
                                    {t('header.logout')}
                                </button>
                            </div>
                            <div className="header-offcanvas-member header-offcanvas-accordion">
                                <button
                                    type="button"
                                    className={`header-offcanvas-list-link header-offcanvas-list-link--trigger${memberPanelAccordionOpen ? ' active' : ''}`}
                                    aria-expanded={memberPanelAccordionOpen}
                                    aria-controls="header-offcanvas-member-panel"
                                    id="header-offcanvas-member-trigger"
                                    onClick={() =>
                                        setMemberPanelAccordionOpen((o) => !o)
                                    }
                                >
                                    <span>{t('header.memberPanel')}</span>
                                    <ChevronDown
                                        size={18}
                                        strokeWidth={2}
                                        aria-hidden
                                        className={`header-offcanvas-trigger-chevron flex-shrink-0${memberPanelAccordionOpen ? ' is-open' : ''}`}
                                    />
                                </button>
                                {memberPanelAccordionOpen ? (
                                    <ul
                                        id="header-offcanvas-member-panel"
                                        className="header-offcanvas-sublist list-unstyled mb-0"
                                        aria-labelledby="header-offcanvas-member-trigger"
                                    >
                                        {MEMBER_AREA_NAV_ITEMS.map((item) => (
                                            <li key={item.to}>
                                                <NavLink
                                                    to={item.to}
                                                    className={({
                                                        isActive,
                                                    }) =>
                                                        `header-offcanvas-list-link header-offcanvas-list-link--sub${isActive ? ' active' : ''}`
                                                    }
                                                    onClick={closeMobileNav}
                                                >
                                                    {t(item.labelKey)}
                                                </NavLink>
                                            </li>
                                        ))}
                                    </ul>
                                ) : null}
                            </div>
                        </div>
                    ) : (
                        <div className="header-offcanvas-user-card">
                            <Link
                                to="/login"
                                className="header-offcanvas-login d-flex align-items-center text-decoration-none"
                                onClick={closeMobileNav}
                            >
                                <CircleUser size={22} aria-hidden className="flex-shrink-0" />
                                <span className="small fw-semibold ms-2">
                                    {t('header.memberLogin')}
                                </span>
                            </Link>
                        </div>
                    )}
                </div>
                <div className="header-menu">
                    <div className="header-offcanvas-site-nav d-lg-none">
                        <ul className="header-offcanvas-list header-offcanvas-list--site m-0 p-0 list-unstyled">
                            <li>
                                <NavLink
                                    to="/"
                                    end
                                    className={({ isActive }) =>
                                        `header-offcanvas-list-link${isActive ? ' active' : ''}`
                                    }
                                    onClick={handleMenuNavigate}
                                >
                                    {t('header.home')}
                                </NavLink>
                            </li>
                            {HEADER_MEGA_MENUS.map((menu) => {
                                const pathActive = headerPathMatches(
                                    pathname,
                                    menu.activePathMatchers,
                                );
                                const triggerActive =
                                    megaOpen === menu.id || pathActive;
                                const subLinks = menu.columns.flat();
                                return (
                                    <li
                                        key={menu.id}
                                        className="header-offcanvas-nav-group"
                                    >
                                        <button
                                            type="button"
                                            className={`header-offcanvas-list-link header-offcanvas-list-link--trigger${triggerActive ? ' active' : ''}`}
                                            aria-expanded={megaOpen === menu.id}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setMegaOpen((cur) =>
                                                    cur === menu.id
                                                        ? null
                                                        : menu.id,
                                                );
                                            }}
                                        >
                                            <span>{t(menu.labelKey)}</span>
                                            <ChevronDown
                                                size={18}
                                                strokeWidth={2}
                                                aria-hidden
                                                className={`header-offcanvas-trigger-chevron flex-shrink-0${megaOpen === menu.id ? ' is-open' : ''}`}
                                            />
                                        </button>
                                        {megaOpen === menu.id ? (
                                            <ul className="header-offcanvas-sublist list-unstyled mb-0">
                                                {subLinks.map((link) => (
                                                    <li key={link.to}>
                                                        <HeaderMenuLink
                                                            to={link.to}
                                                            className="header-offcanvas-list-link header-offcanvas-list-link--sub"
                                                            onNavigate={
                                                                handleMenuNavigate
                                                            }
                                                        >
                                                            {t(link.linkKey)}
                                                        </HeaderMenuLink>
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : null}
                                    </li>
                                );
                            })}
                            {HEADER_SIMPLE_LINKS.map((item) => (
                                <li key={item.to}>
                                    <NavLink
                                        className={({ isActive }) =>
                                            `header-offcanvas-list-link${isActive ? ' active' : ''}`
                                        }
                                        to={item.to}
                                        end={item.end === true}
                                        onClick={handleMenuNavigate}
                                    >
                                        {t(item.linkKey)}
                                    </NavLink>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="container py-2 position-relative d-none d-lg-block">
                        <ul className="header-menu-nav m-0 p-0 list-unstyled">
                            <li
                                className="header-menu-item header-menu-item--simple"
                                onMouseEnter={closeMegaNow}
                            >
                                <NavLink
                                    to="/"
                                    end
                                    className={({ isActive }) =>
                                        `header-menu-plain-link${isActive ? ' is-active' : ''}`
                                    }
                                    onClick={handleMenuNavigate}
                                >
                                    {t('header.home')}
                                </NavLink>
                            </li>
                            {HEADER_MEGA_MENUS.map((menu) => {
                                const pathActive = headerPathMatches(
                                    pathname,
                                    menu.activePathMatchers,
                                );
                                const triggerActive =
                                    megaOpen === menu.id || pathActive;
                                return (
                                    <li
                                        key={menu.id}
                                        className="header-menu-item"
                                        onMouseEnter={() => {
                                            if (
                                                window.matchMedia(
                                                    '(min-width: 992px)',
                                                ).matches
                                            ) {
                                                openMegaHover(menu.id);
                                            }
                                        }}
                                    >
                                        <button
                                            type="button"
                                            className={`header-menu-trigger${triggerActive ? ' is-active' : ''}`}
                                            aria-expanded={megaOpen === menu.id}
                                            aria-controls="header-mega-panel"
                                            id={`header-mega-trigger-${menu.id}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                clearCloseTimer();
                                                setMegaOpen(menu.id);
                                            }}
                                        >
                                            {t(menu.labelKey)}
                                        </button>
                                    </li>
                                );
                            })}
                            {HEADER_SIMPLE_LINKS.map((item) => (
                                <li
                                    key={item.to}
                                    className="header-menu-item header-menu-item--simple"
                                    onMouseEnter={closeMegaNow}
                                >
                                    <NavLink
                                        className={({ isActive }) =>
                                            `header-menu-plain-link${isActive ? ' is-active' : ''}`
                                        }
                                        to={item.to}
                                        end={item.end === true}
                                        onClick={handleMenuNavigate}
                                    >
                                        {t(item.linkKey)}
                                    </NavLink>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div
                        id="header-mega-panel"
                        className={`header-mega-panel${megaOpen ? ' is-open' : ''}`}
                        role="region"
                        aria-hidden={!megaOpen}
                        aria-labelledby={
                            megaOpen
                                ? `header-mega-trigger-${megaOpen}`
                                : undefined
                        }
                        onMouseEnter={clearCloseTimer}
                    >
                        {activeMega && (
                            <div className="container py-4 py-lg-5">
                                <div className="row g-4 g-lg-5 align-items-stretch">
                                    <div className="col-lg-4">
                                        <div className="header-mega-visual h-100 rounded-4 p-4 p-lg-4 text-center text-lg-start">
                                            <img
                                                src="/assets/images/rotary-logo.svg"
                                                alt=""
                                                className="header-mega-visual-logo d-block mx-auto mx-lg-0 mb-3"
                                            />
                                            <h3 className="header-mega-visual-title h5 mb-2">
                                                {t(activeMega.introTitleKey)}
                                            </h3>
                                            <p className="header-mega-visual-text small mb-0 text-secondary">
                                                {t(activeMega.introTextKey)}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="col-lg-8">
                                        <div className="row g-4">
                                            {activeMega.columns.map((col, ci) => (
                                                <div
                                                    key={ci}
                                                    className={
                                                        activeMega.columns.length ===
                                                            1
                                                            ? 'col-12'
                                                            : 'col-md-6'
                                                    }
                                                >
                                                    <ul className="list-unstyled header-mega-link-col mb-0">
                                                        {col.map((link) => (
                                                            <li
                                                                key={link.to}
                                                                className="header-mega-link-row"
                                                            >
                                                                <HeaderMenuLink
                                                                    to={link.to}
                                                                    className="header-mega-link"
                                                                    onNavigate={
                                                                        handleMenuNavigate
                                                                    }
                                                                >
                                                                    {t(
                                                                        link.linkKey,
                                                                    )}
                                                                </HeaderMenuLink>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </header>
    );
}
